/**
 * THE RAIL'S SECOND LINE, KEPT ON THE DEVICE — owner, 2026-08-22: 幫我把 Side Panel
 * Agent preview + Clean View 在前端設計一個 Cache 的機制…這樣每次重刷就不需要重讀
 * 一整串了.
 *
 * The rail already holds a `lastSeen` map so a TRANSIENT gap in the idle poll does not
 * blank a row (see Sidebar's note on `transcript_ambiguous`). That map is a `useRef`,
 * so a refresh empties it: every row loses its preview and waits for a poll that has to
 * reach each machine before a single line of it can be drawn. On a slow link that is
 * seconds of a list that says nothing.
 *
 * SMALL BY CONSTRUCTION. One short line per agent, so the whole map is tens of KB and
 * belongs in localStorage — unlike the transcript cache beside it, which holds blocks
 * and needs a real database.
 *
 * WHAT IT IS NOT: a source of truth. A cached line is the last thing this device saw,
 * and the poll overwrites it the moment it answers. It is a first paint, and the rules
 * about when a preview must be BLANK (the server saying `no_transcript`,
 * `transcript_stale`, `nothing_yet`) stay where they are — this only fills the gap
 * where the server has said nothing at all, which is the same gap `lastSeen` fills.
 */

const STORE = 'wt_previews'
/** A preview is one line; the rail truncates it anyway. */
const MAX_CHARS = 200
/** More agents than any account has shown so far, and still only tens of KB. */
const MAX_ROWS = 200

export function loadPreviews(): Map<string, string> {
  try {
    const raw = window.localStorage.getItem(STORE)
    if (!raw) return new Map()
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return new Map()
    const out = new Map<string, string>()
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string' && v) out.set(k, v)
    }
    return out
  } catch {
    return new Map()
  }
}

/**
 * Write the map back, newest-kept.
 *
 * Insertion order is the Map's own, and the caller sets entries as it sees them, so
 * the tail of the map is the most recently seen — which is what to keep when there are
 * too many. A row for an agent that no longer exists costs one line and is dropped by
 * this cap rather than by a liveness check the rail would have to invent.
 */
export function savePreviews(previews: Map<string, string>): void {
  try {
    const entries = [...previews.entries()].filter(([k, v]) => k && v)
    const kept = entries.slice(Math.max(0, entries.length - MAX_ROWS))
    const obj: Record<string, string> = {}
    for (const [k, v] of kept) obj[k] = v.slice(0, MAX_CHARS)
    window.localStorage.setItem(STORE, JSON.stringify(obj))
  } catch {
    /* storage unavailable or full — a blank second line is not worth an exception */
  }
}
